import { PREVIEW_MODE } from "@/lib/previewMode";

export interface MatchInfo {
  id: string;
  homeCode: string;
  awayCode: string;
  homeFlag: string;
  awayFlag: string;
  homeColor: string;
  awayColor: string;
  // simulation offsets so each match feels independent
  startOffset: number; // seconds added to the global clock
  // numeric provider fixture id for real stats (e.g. API-Football)
  providerFixtureId?: number;
  // Marks hard-coded demo/test matches that must NOT be shown to public
  // users unless explicit demo/QA mode is enabled. See useDemoModeAllowed.
  isSynthetic?: boolean;
}

// PREVIEW_MODE — during the World Cup Final private preview the app must
// surface ONLY Spain vs Argentina. The synthetic radar/pitch keeps working
// but is relabeled to ESP/🇪🇸 vs ARG/🇦🇷. Flip PREVIEW_MODE off to restore
// the full multi-match roster without editing this file.
const ALL_MATCHES: MatchInfo[] = [
  { id: "mex-arg", homeCode: PREVIEW_MODE ? "ESP" : "MEX", awayCode: "ARG", homeFlag: PREVIEW_MODE ? "🇪🇸" : "🇲🇽", awayFlag: "🇦🇷", homeColor: PREVIEW_MODE ? "#e11d48" : "#10b981", awayColor: "#60a5fa", startOffset: 0, providerFixtureId: PREVIEW_MODE ? 1591866 : undefined, isSynthetic: true },
  { id: "bra-ger", homeCode: "BRA", awayCode: "GER", homeFlag: "🇧🇷", awayFlag: "🇩🇪", homeColor: "#facc15", awayColor: "#9ca3af", startOffset: 17, isSynthetic: true },
  { id: "esp-fra", homeCode: "ESP", awayCode: "FRA", homeFlag: "🇪🇸", awayFlag: "🇫🇷", homeColor: "#ef4444", awayColor: "#3b82f6", startOffset: 33, isSynthetic: true },
  { id: "usa-col", homeCode: "USA", awayCode: "COL", homeFlag: "🇺🇸", awayFlag: "🇨🇴", homeColor: "#3b82f6", awayColor: "#fbbf24", startOffset: 51, isSynthetic: true },
  { id: "eng-por", homeCode: "ENG", awayCode: "POR", homeFlag: "🏴󠁧󠁢󠁥󠁮󠁧󠁿", awayFlag: "🇵🇹", homeColor: "#f8fafc", awayColor: "#16a34a", startOffset: 73, isSynthetic: true },
];

export const MATCHES: MatchInfo[] = PREVIEW_MODE
  ? ALL_MATCHES.filter((m) => m.id === "mex-arg")
  : ALL_MATCHES;

export interface MatchSnapshot {
  id: string;
  homeScore: number;
  awayScore: number;
  minute: number;
  status: "SCHEDULED" | "LIVE" | "HT" | "FT";
  /** ISO kickoff time, used when status === "SCHEDULED" to label the carousel. */
  kickoff?: string;
  recentEvent?: { type: "goal" | "shot" | "danger" | "penalty"; team: "home" | "away"; at: number };
}

/** Derive a deterministic snapshot for any secondary match without running a full sim. */
export function snapshotFor(match: MatchInfo, globalElapsed: number): MatchSnapshot {
  const t = (globalElapsed + match.startOffset) % 120;
  // Goal cadence per match (using hashed offsets so they don't all goal at once).
  const hash = match.id.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const homeGoals = Math.floor(((t + (hash % 13)) * 1.0) / 45);
  const awayGoals = Math.floor(((t + (hash % 19)) * 0.8) / 55);
  const minute = Math.min(90, 30 + Math.floor(t / 1.4));
  const status: MatchSnapshot["status"] = t > 100 ? "FT" : t > 55 && t < 60 ? "HT" : "LIVE";
  // Recent event window
  let recentEvent: MatchSnapshot["recentEvent"];
  const eventTick = Math.floor(t / 11);
  const phase = (t - eventTick * 11) | 0;
  if (phase < 2) {
    const types = ["goal", "shot", "danger", "penalty"] as const;
    const type = types[(hash + eventTick) % types.length];
    recentEvent = { type, team: (hash + eventTick) % 2 === 0 ? "home" : "away", at: t };
  }
  return { id: match.id, homeScore: homeGoals, awayScore: awayGoals, minute, status, recentEvent };
}
